import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

const CART_KEY = 'tfd_cart';

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponInfo, setCouponInfo] = useState(null);

  useEffect(() => { saveCart(items); }, [items]);

  const addItem = useCallback((item) => {
    const id = `cart_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setItems(prev => [...prev, { ...item, cartId: id }]);
    return id;
  }, []);

  const removeItem = useCallback((cartId) => {
    setItems(prev => prev.filter(i => i.cartId !== cartId));
  }, []);

  const updateItem = useCallback((cartId, updates) => {
    setItems(prev => prev.map(i => i.cartId === cartId ? { ...i, ...updates } : i));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCouponCode('');
    setCouponDiscount(0);
    setCouponInfo(null);
    localStorage.removeItem(CART_KEY);
  }, []);

  // Feb 2026: rehydrate the cart from a pending server-side order after the
  // customer clicks "Resume checkout" in the 12h abandonment reminder email.
  // Replaces the current cart wholesale so the checkout page renders the
  // items exactly as they were submitted.
  const replaceCart = useCallback((serverItems) => {
    const rebuilt = (serverItems || []).map((it, idx) => ({
      ...it,
      cartId: `cart_resume_${Date.now()}_${idx}`,
      base_price_cents: it.price_cents,
    }));
    setItems(rebuilt);
  }, []);

  const getSubtotal = useCallback(() => {
    return items.reduce((sum, item) => {
      // CHK-PRICE-01: honor the per-item base_price_cents the catalog resolved
      // at add-to-cart time, falling back to the service-family default only
      // when it's absent. The CartPage line display already does this, so the
      // summary total now agrees with the sum of the visible line prices.
      let base = item.base_price_cents || (item.service_family === 'authentication' ? 2000 : 5000);
      if (item.is_expedited && item.service_family === 'authentication') base += 2000;
      if (item.wants_mailed_document) base += 1000;
      return sum + base;
    }, 0);
  }, [items]);

  const getTotal = useCallback(() => {
    return Math.max(0, getSubtotal() - couponDiscount);
  }, [getSubtotal, couponDiscount]);

  const itemCount = items.length;

  return (
    <CartContext.Provider value={{
      items, itemCount, addItem, removeItem, updateItem, clearCart, replaceCart,
      getSubtotal, getTotal, couponCode, setCouponCode,
      couponDiscount, setCouponDiscount, couponInfo, setCouponInfo,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

export default CartContext;
